import {useZero} from '@/shared/integrations/zero/use-zero'
import type {PatientSource} from '@cypro/engine/database/schema'
import {findDuplicatePatientIdentifiers, type PatientIdentifierValues} from '@cypro/engine/utils'
import {useDebouncedState} from '@cypro/ui/hooks/use-debounced-state'
import {queries} from '@cypro/engine/queries'
import {useQuery, useZero as useZeroClient} from '@rocicorp/zero/react'

const DEBOUNCE_MS = 500

interface LookupOptions {
  excludePatientId?: string
  /** defaults to local patients (manual and imported from HL7) */
  sources?: PatientSource[]
}

function toQueryInput(values: PatientIdentifierValues, {excludePatientId, sources}: LookupOptions = {}) {
  return {
    nationalId: values.nationalId || undefined,
    hospitalPatientId: values.hospitalPatientId || undefined,
    insuranceNumber: values.insuranceNumber || undefined,
    excludePatientId,
    sources,
  }
}

export type DuplicatePatientMatches = ReturnType<typeof useDuplicatePatientIdentifiers>

/** Each matched patient once, even when it matches several numbers. */
export function uniqueMatchedPatients<T extends {id: string}>(matches: Partial<Record<string, T>>): T[] {
  const byId = new Map<string, T>()
  for (const patient of Object.values(matches)) if (patient) byId.set(patient.id, patient)
  return [...byId.values()]
}

/** Live lookup of patients already using the typed national ID, hospital ID or insurance number. */
export function useDuplicatePatientIdentifiers(values: PatientIdentifierValues, options: LookupOptions = {}) {
  const zero = useZero()
  const nationalId = useDebouncedState(values.nationalId, DEBOUNCE_MS)
  const hospitalPatientId = useDebouncedState(values.hospitalPatientId, DEBOUNCE_MS)
  const insuranceNumber = useDebouncedState(values.insuranceNumber, DEBOUNCE_MS)

  const debounced = {nationalId, hospitalPatientId, insuranceNumber}
  const [candidates] = useQuery(
    zero.queries.patients.findPatientsByIdentifiers(toQueryInput(debounced, options))
  )

  return findDuplicatePatientIdentifiers(candidates, debounced)
}

/** One-off lookup that waits for the server result, so it is not affected by the debounce or partial sync. */
export function useFindDuplicatePatientIdentifiers() {
  // the app's useZero spreads the client into a plain object, which drops prototype methods like run()
  const zero = useZeroClient()

  return async function find(values: PatientIdentifierValues, excludePatientId?: string) {
    const candidates = await zero.run(
      queries.patients.findPatientsByIdentifiers(toQueryInput(values, {excludePatientId})),
      {type: 'complete'}
    )
    return findDuplicatePatientIdentifiers(candidates, values)
  }
}

/**
 * Check run on submit, so a number typed just before saving (still inside the debounce window) is not missed.
 * Focuses and returns the first duplicated field, if any.
 */
export function useCheckDuplicatePatientIdentifiers() {
  const findDuplicates = useFindDuplicatePatientIdentifiers()

  return async function check(values: PatientIdentifierValues, excludePatientId?: string) {
    const duplicates = await findDuplicates(values, excludePatientId)
    const field = (Object.keys(duplicates) as (keyof typeof duplicates)[])[0]
    if (field) document.querySelector<HTMLInputElement>(`input[name="${field}"]`)?.focus()
    return field
  }
}
